// The quote→derived conversion for card fields the apply writes as primitives,
// plus the one field (`dod_criteria`) whose "conversion" is identity and whose
// only structural bound — a length cap on what the agent may PROPOSE — is
// declared here as a mint-only bound.
//
// WHY THIS FILE EXISTS. A card field's persisted value is `string`
// (`fieldStateSchema.value`), and the readiness spec describes a field with
// `{ label, order, narrative, required }` — no value type, no validator. So the
// invariant "a string the gate greened for `story_points` can be turned into a
// number by the apply" spans two modules that share no type, and TypeScript
// cannot express it: `"Small"` is a perfectly good `string`.
//
// It broke exactly that way. A refine flow greened `story_points` with the word
// `"Small"` — `resolvedSource: "user"`, quoted verbatim out of the user's prose,
// so the grounding gate was working correctly — and the apply then threw
// `reconstruct_failed` on every attempt, including every retry, because the card
// state never changes. The user had a green card and a permanently dead Accept
// button.
//
// The fix is to make "green" and "writable" the SAME function rather than two
// implementations that agree by convention. `checkFieldValue` is defined as
// "`reconstruct` returns non-null", so the gate cannot green a value the apply
// would reject, and neither can drift from the other without the compiler
// noticing. Keying by field NAME (not by a spec-declared validator id) is
// deliberate: apply-mapping is already keyed by field name, and adding a third
// keyed surface would just be one more thing to drift.
//
// Reasons returned here ride to the model and to the client, so they name the
// expected SHAPE and never echo the offending value (content-free).
//
// NB the table is no longer only about primitives. `dod_criteria` lands in the
// description VERBATIM — the reconstruction is identity apart from "not empty" —
// and it is here so its length is bounded by the same machinery every green path
// already runs. `role` and `means` are here on the same footing with a different
// bound: they land verbatim too, and what they need bounded is their SHAPE,
// because a whole sentence in either slot is written out as "As a <sentence>, I
// want …" / "I want <sentence>, so that …".
//
// TWO PREDICATES, DELIBERATELY. `reconstruct()` answers "could the apply write
// this?" and is the ONLY thing a re-confirmation of an already-persisted green is
// allowed to re-test (`checkPersistedFieldValue`). `mintBound` adds bounds that
// apply to a value the FIRST time it is proposed (`checkFieldValue`). The split
// exists so that tightening a bound can never retro-downgrade a card the user
// already got to green — see `mintBound`'s docblock.
//
// The second half of the file is the OTHER end of the same job: `reconstruct`
// answers "can this value be written", the narrative-splice helpers answer "how
// does it read once it IS written", for the three fields that land inside fixed
// story scaffolding rather than in a field of their own.

import { looksNonEnglish } from "../i18n.js";
import { parseStoryPointsValue, STORY_POINT_SCALE } from "./sizing.js";

/**
 * "Priority: High" / "high" / "p1" → the priority label enum — and the same
 * three in German, the other language Loom speaks: "Hoch", "Niedrig", "Mittel".
 *
 * The German words are the labels Loom's own priority buttons show a German
 * user (`priority.high` → "Hoch"), so they are words the user was offered. A
 * chat has no buttons, so there the user TYPES the word, and the value the gate
 * checks is the user's own bytes either way: only the MAPPING to the enum was
 * English-only. Live on dev (sessions d6e8e43b, 29ef6027, 0d3b8ce3) "Priorität:
 * Hoch", "Hoch", "hoch" and "Die Priorität ist hoch." never greened — "Hoch"
 * failed this parse, and the model's "High" was not in any user turn — until
 * the user typed the English word, after being told to "nicht auf Deutsch
 * übersetzen". The customer's stuck `role`, moved to this field.
 */
export const parseCardPriority = (
  v: string | undefined,
): "high" | "normal" | "low" | null => {
  if (!v) return null;
  const s = v.toLowerCase();
  if (/\b(high|urgent|p1|hoch|dringend)\b/u.test(s)) return "high";
  if (/\b(low|p3|niedrig|tief|gering)\b/u.test(s)) return "low";
  if (/\b(normal|medium|p2|mittel)\b/u.test(s)) return "normal";
  return null;
};

/** "5 points" / "5" → 5. Adapts the shared parser's `number | undefined` to the
 *  `T | null` reconstruct contract. */
export const parseCardStoryPoints = (v: string | undefined): number | null => {
  const n = parseStoryPointsValue(v);
  return n === undefined ? null : n;
};

/** Turns a greened card value into the primitive the apply writes. `null` means
 *  "this string is not writable", which is exactly what makes the slot not green. */
export interface FieldReconstructor<T> {
  readonly reconstruct: (v: string | undefined) => T | null;
  /** Shape-only explanation for a rejected value. Never echoes the value. */
  readonly reason: () => string;
  /**
   * An additional bound checked ONLY when a value is first proposed, and
   * deliberately NOT re-checked when an already-persisted green is re-confirmed.
   *
   * A bound in `reconstruct` is a claim about the apply ("this string cannot be
   * written"), so re-testing it on every merge is right — the row is broken and
   * a green that survives would strand the Accept button. A bound HERE is a
   * policy about what may go into the field at all, so re-testing it on every
   * merge would mean tightening the policy silently downgrades cards the user
   * already got to green in a session started before the deploy — turning a
   * finished card into a fresh question about something they never asked about.
   * Mint-only means the tightening applies going forward and to nothing else, so
   * shipping it needs no census of what is already persisted.
   */
  readonly mintBound?: {
    readonly ok: (v: string | undefined) => boolean;
    /** Shape-only, and a QUESTION: this string can reach the user. */
    readonly reason: () => string;
  };
}

/**
 * Upper bound on a model-authored Definition of Done, in characters.
 *
 * MINT-ONLY (see `FieldReconstructor.mintBound`). Chosen an order of magnitude
 * above the "one or two sentences" the PM directive asks for, so it bites on a
 * runaway and not on a verbose-but-sane bar. It is a ceiling, not a measurement:
 * nobody has counted the DoDs already persisted, and `mintBound` is what makes
 * that census unnecessary.
 */
export const DOD_MAX_CHARS = 2000;

/**
 * Upper bound on a role, in characters. MINT-ONLY.
 *
 * A role names an audience — "finance manager", "the nightly billing job". The
 * cap is a runaway backstop, not a style rule: it is roughly three times the
 * longest sane role and is there so a paragraph pasted into the slot is refused
 * by something other than luck.
 */
export const ROLE_MAX_CHARS = 120;

/** Whitespace-separated tokens, below which a trailing period is more likely an
 *  abbreviation or a typo in a job title than a sentence: "Product owner, Acme
 *  Inc." keeps its green, a standup anecdote does not. Comfortably under the
 *  shortest offender seen live (12 tokens) and over an ordinary title. */
const ROLE_SENTENCE_MIN_WORDS = 6;

/**
 * Whole values that AGREE rather than NAME: a reply to "who is this for?" that
 * says yes to something and names nobody. Compared against the whole value,
 * lower-cased, with punctuation and surrounding whitespace dropped — never as a
 * substring, so "Confirmation clerks" and "Ok-Team" are untouched. Closed and
 * short on purpose, like {@link ACTIVITY_PARTICLES}: every entry widens what is
 * refused. English and German, the two languages Loom speaks.
 *
 * Live on dev (session 47b36f0b, #143) "I confirm" typed into the Who question's
 * own box greened `role` as "I confirm"; the story read "As I confirm, I want …"
 * until a later review caught it. On the prod ticket that started this, "I
 * confirm" was the first of four answers to the same question.
 */
const ROLE_ASSENTS: ReadonlySet<string> = new Set([
  "i confirm",
  "confirmed",
  "confirm",
  "yes",
  "ok",
  "okay",
  "fine",
  "agreed",
  "sounds good",
  "looks good",
  "same as before",
  "you decide",
  "ich bestätige",
  "bestätigt",
  "bestätige",
  "ja",
  "passt",
  "passt so",
  "ja passt",
  "ja passt so",
  "stimmt",
  "genau",
  "einverstanden",
  "wie vorher",
  "du entscheidest",
]);

/**
 * Is this whole message a bare assent — "I confirm", "ok", "passt so", "Ich
 * bestätige" — that agrees to something and names nothing? The same closed list
 * and the same normalisation `isRoleShaped` refuses a role by.
 *
 * Its other reader is `card_status`, for the Stop hook: such a message can
 * never ground a card value (a value is the user's own bytes, and an assent
 * carries none of the value it agrees to), so an end-of-turn block that asks
 * the agent to ground it buys nothing but a second reply to it — issue page
 * #172 and #173, one extra message each on "Ich bestätige" and "I confirm".
 */
export const isBareAssent = (text: string): boolean => isAssent(text);

const isAssent = (s: string): boolean =>
  ROLE_ASSENTS.has(
    s
      .toLowerCase()
      .replace(/[.,!?;:"'’“”„()\-–—]+/gu, " ")
      .replace(/\s+/gu, " ")
      .trim(),
  );

/** Particles that turn a leading `-ing` word into an ACTIVITY rather than a job
 *  title: "Digging up …" is a thing someone does, "Marketing team lead" is a
 *  person. The list is closed and short on purpose — every entry widens what
 *  gets refused. */
const ACTIVITY_PARTICLES: ReadonlySet<string> = new Set([
  "up",
  "out",
  "through",
  "into",
  "down",
  "over",
  "around",
  "back",
]);

/**
 * Does this value have the SHAPE of a role — a name for a person, team or
 * system — rather than a sentence about one?
 *
 * SHAPE ONLY, and deliberately so. This function is sync, single-field, and
 * ships in the SPA bundle; it cannot know whether "the wider team" is the right
 * audience, and it must not pretend to. It only refuses values whose FORM is not
 * a name:
 *
 *   • a sentence — terminal `.` / `!` / `?` on something long enough that the
 *     punctuation is not a typo. Live #82 greened "The wider team - we go
 *     through the list together in standup." and live #78 greened "Every team
 *     member should see their own filter — it's personal to each person, not
 *     shared across the team.", both of which then read as `As a <sentence>, I
 *     want …` in the issue description;
 *   • an ACTIVITY phrase — a gerund plus a particle. Live #81 greened "Digging
 *     up an old issue on the project dashboard", which names what the user was
 *     doing, not who they are;
 *   • a runaway (`ROLE_MAX_CHARS`);
 *   • an ASSENT — "I confirm", "ok", "passt so" — which agrees to something
 *     and names nobody ({@link ROLE_ASSENTS}, whole value only).
 *
 * Everything else passes. That asymmetry is the design: a role that reads oddly
 * but is a name costs nobody anything, while a false refusal puts a question in
 * front of a user who already answered it. Two consequences worth naming rather
 * than discovering — the activity-phrase rule is English-shaped and does nothing
 * on a German session (the other two are language-neutral), and a role that is a
 * bare noun phrase naming the WRONG audience is not this function's business and
 * never will be.
 */
export const isRoleShaped = (v: string | undefined): boolean => {
  const s = (v ?? "").trim();
  if (s === "") return false;
  if (s.length > ROLE_MAX_CHARS) return false;
  if (isAssent(s)) return false;
  const words = s.split(/\s+/u);
  if (
    words.length >= ROLE_SENTENCE_MIN_WORDS &&
    /[.!?]["'’”)]?$/u.test(s)
  ) {
    return false;
  }
  const [first, second] = words;
  if (
    first !== undefined &&
    second !== undefined &&
    /^[A-Za-z]+ing$/u.test(first) &&
    ACTIVITY_PARTICLES.has(second.toLowerCase())
  ) {
    return false;
  }
  return true;
};

/**
 * The fields whose greened value must survive a conversion. A field ABSENT here
 * is free-text to the apply (`ends` / `acceptance_criteria` land in the issue
 * description verbatim) and is never shape-checked.
 *
 * `role`, `means` and `dod_criteria` are here for a SHAPE, not a conversion:
 * their reconstruction is identity, and what the entry buys is the mint-only
 * bound underneath it.
 */
export const FIELD_RECONSTRUCTORS: Readonly<
  Record<string, FieldReconstructor<unknown>>
> = {
  dod_criteria: {
    // The DoD is the one card field the AGENT authors — the PM directive has it
    // decide the quality bar rather than put a testing question in front of a
    // business owner — and it has no reconstruction: it lands in the issue
    // description VERBATIM with no user quote behind it. Length is therefore the
    // only structural bound available.
    //
    // Writability is just "there is something to write": apply renders whatever
    // is green, so any non-empty string writes fine, including one longer than
    // the cap. The cap lives in `mintBound` below.
    reconstruct: (v) => (v !== undefined && v.trim().length > 0 ? v : null),
    // Model-facing like every other `reason` here — Focus renders the field's
    // own localized question for a `gateRefusal` verdict, never this. Kept as a
    // question anyway: it is what the AGENT reads to learn what is missing, and
    // an imperative reads as an order to invent one.
    reason: () => "Can you say in one or two sentences what 'done' means here?",
    // The length cap, enforced on every value proposed from here on and on no
    // value already persisted green. Two consequences, both intended:
    //   • a NEW over-cap proposal — from the agent OR from a user answer — comes
    //     back `needs_input`, and that verdict carries no value, so no green
    //     persists and nothing over-cap can be inherited by a later card;
    //   • a card that went green under an earlier deploy keeps its DoD, however
    //     long, and re-confirms on later merges like any other green. It is
    //     grandfathered on purpose: nobody has counted the persisted values (that
    //     would need a decrypt over prod data), and downgrading one turns a
    //     finished card into a DoD question — the technical question PM mode
    //     exists to avoid — for a user who did nothing wrong.
    // The cap is not optional; it is prospective.
    mintBound: {
      ok: (v) => (v ?? "").length <= DOD_MAX_CHARS,
      // MODEL-FACING. A `mintBound` refusal is the server refusing a value the
      // AGENT proposed, so the verdict carrying this string is stamped
      // `gateRefusal` and no surface renders it (see `SlotVerdict.gateRefusal`);
      // the user, if the refusal ever survives to them, gets the field's own
      // localized question. That is the right audience: the DoD is the one field
      // the agent authors, so the overrun is almost always the agent's, and
      // naming a character limit to a business owner for prose they did not
      // write is the amnesia the flag exists to stop. The bound is stated to the
      // model where it belongs, in `PROPOSE_CARD_TOOL_DESCRIPTION`.
      // Content-free: the limit, never the value.
      // "at most", not "under": the predicate above is `<=`, so a value of
      // exactly DOD_MAX_CHARS passes. A reason that misstates the bound by one
      // is a reason someone re-answers to and gets refused again.
      reason: () =>
        `Can you say in one or two sentences what 'done' means here? Keep it to at most ${DOD_MAX_CHARS} characters.`,
    },
  },
  role: {
    // Free text to the apply: the value is spliced into the story sentence
    // verbatim ("As a <role>, I want … so that …", per locale template), so
    // writability is just "there is something to write". Everything
    // structural about it lives in `mintBound` below, which is what keeps this
    // entry from retro-downgrading the roles already persisted green.
    reconstruct: (v) => (v !== undefined && v.trim().length > 0 ? v : null),
    reason: () => "Who is this for?",
    // The shape contract. It is checked ONLY when a role is first proposed — an
    // already-green role is re-confirmed by `reconstruct` alone, so shipping
    // this cannot turn a finished card into a fresh question about a field the
    // user already answered.
    mintBound: {
      ok: isRoleShaped,
      // MODEL-FACING, and this string is the reason the flag exists. It used to
      // reach the USER as the question headline: they answered `role` in German,
      // the model quoted their whole sentence, this refused it, and they were
      // shown this English sentence under the same kicker as the first ask —
      // while the model went on to trim its own quote 27 seconds later and green
      // the field with no further input. The verdict is now stamped
      // `gateRefusal` (see `SlotVerdict.gateRefusal`), so a surface that knows
      // the user answered suppresses it and lets the model repair itself.
      // Content-free: never echoes the value.
      reason: () =>
        "Who is this for? Name them in a few words — a person, a team or a system, not a sentence.",
    },
  },
  means: {
    // Free text to the apply, exactly like `role`: the value is spliced into the
    // story sentence ("I want {means}"), so writability is just "there is
    // something to write". The shape bound is below, and it is mint-only for the
    // usual reason — tightening it must not re-open a card someone already
    // finished.
    reconstruct: (v) => (v !== undefined && v.trim().length > 0 ? v : null),
    reason: () => "What should they be able to do?",
    mintBound: {
      // Wrapped rather than passed by reference: `isMeansShaped` is declared in
      // the splice half of this module, below this table, so naming it here at
      // module-init time would read it in its temporal dead zone. The arrow
      // resolves at CALL time, which is the only time this runs.
      ok: (v) => isMeansShaped(v),
      // MODEL-FACING (the verdict is stamped `gateRefusal`, so Focus renders the
      // field's own localized question instead — see `SlotVerdict.gateRefusal`).
      // It names the SHAPE and the way out: grounding is substring containment,
      // so a narrower span of the same turn is always available. Content-free.
      reason: () =>
        "What should they be able to do? Quote the capability as a phrase that " +
        "completes \"I want …\" — not a whole sentence with its own subject and " +
        "verb, and not a bare command (\"export the data\"): write it as \"to " +
        "export the data\" or \"the ability to export the data\" instead. " +
        "Grounding accepts any verbatim substring of a user turn, so a " +
        "narrower quote is always safe.",
    },
  },
  story_points: {
    reconstruct: parseCardStoryPoints,
    reason: () =>
      `Story points must be one of ${STORY_POINT_SCALE.join(", ")} — a number, not a size word.`,
  },
  priority: {
    reconstruct: parseCardPriority,
    // MODEL-FACING. It names the user's own word as a way in: the value the
    // gate checks is the user's bytes, so "High" for a user who wrote "Hoch"
    // is refused as not in any user turn — the German-priority dead end.
    reason: () =>
      "Priority must be High, Normal or Low — quote the user's own word for " +
      "it (German: Hoch, Normal, Niedrig), never a translation of it.",
  },
};

/** Whether this field's value has to survive a reconstruction to count as green. */
export const isReconstructableField = (field: string): boolean =>
  Object.hasOwn(FIELD_RECONSTRUCTORS, field);

export type FieldValueCheck =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly reason: string;
      /**
       * WHICH HALF REFUSED. `"mint"` means the value is perfectly writable and
       * it was a `mintBound` — a SHAPE preference this deploy added — that said
       * no; absent means the apply genuinely could not write it.
       *
       * The two cannot be treated alike once a user has spoken. A shape refusal
       * on a value the user themselves authored LATER than the green it
       * replaces has no way out: the refusal is model-facing
       * (`SlotVerdict.gateRefusal`), the merge keeps the old green, and the
       * user's own correction disappears with the card still reading
       * `allGreen`. See the supersede rule in `propose-card.ts`'s merge — it
       * keys on exactly this flag, and only ever on it.
       */
      readonly bound?: "mint";
    };

/**
 * The MINT predicate: may this value become green NOW? `ok` for every field with
 * no reconstructor; otherwise `ok` iff the apply could write it AND it satisfies
 * the field's mint-only bounds.
 *
 * Run this on every path that can mint a green from a value seen for the first
 * time — fresh slot resolution, the auto-ground backstop, the direct field edit.
 * Re-confirming an inherited green runs `checkPersistedFieldValue` instead.
 */
export const checkFieldValue = (
  field: string,
  value: string | undefined,
): FieldValueCheck => {
  const r = FIELD_RECONSTRUCTORS[field];
  if (!r) return { ok: true };
  if (r.reconstruct(value) === null) return { ok: false, reason: r.reason() };
  if (r.mintBound && !r.mintBound.ok(value)) {
    return { ok: false, reason: r.mintBound.reason(), bound: "mint" };
  }
  return { ok: true };
};

/**
 * The RE-CONFIRM predicate: is an ALREADY-PERSISTED green still writable?
 * Strictly weaker than `checkFieldValue` — same reconstruction, without the
 * mint-only bounds, which are grandfathered for exactly the reason
 * `FieldReconstructor.mintBound` gives.
 *
 * Every re-confirm path must still run THIS one. Skipping it would leave a
 * persisted unwritable green (`story_points: "Small"`) re-greening forever with
 * a dead Accept button, which is the population that got stranded — and note
 * that grandfathering does not rescue that row: an unwritable value fails here
 * too. Only bounds declared `mintBound` are forgiven.
 */
export const checkPersistedFieldValue = (
  field: string,
  value: string | undefined,
): FieldValueCheck => {
  const r = FIELD_RECONSTRUCTORS[field];
  if (!r) return { ok: true };
  return r.reconstruct(value) !== null ? { ok: true } : { ok: false, reason: r.reason() };
};

// ---------------------------------------------------------------------------
// The narrative SPLICE seam — where fixed scaffolding meets a verbatim value.
//
// Three card fields are not written to a field of their own: `role`, `means`
// and `ends` are spliced into a one-line user story ("As {role}, I want
// {means}, so that {ends}."). Since the F1 verbatim-value rule, those values are
// the user's OWN sentences, and a sentence does not slot into a slot. Issue #3
// on the live dev project was written as
//
//   "…, I want ziel ist ein datenschutzkonformer Login … verloren geht., so
//    that damit die Organisation …"
//
// — one join, four defects: a German noun de-capitalized by an English-shaped
// lowercase rule, a full stop the value already carried welded to the template's
// comma, and the template's connective stacked on top of the connective the
// user's own answer opened with. The same doubling reproduces in plain English
// ("so that so we are ready to integrate …"), so this is a JOIN defect, not a
// German one.
//
// THE INVARIANT. The user's WORDS are byte-exact and are not this seam's to
// touch. What this seam owns is the punctuation and connectives the TEMPLATE
// contributes: it may decline to emit its own connective, and it may drop a full
// stop that sits exactly where the template puts a comma. It never rewrites,
// translates or truncates the value, and the one case fix it makes is refused
// the moment the clause looks like it is not in the template's language.
//
// NFC, once, at the top: `normalizeQuote` (the grounding comparator) NFC-folds
// BOTH sides, so a canonical recomposition here cannot cost a green, and it lets
// the connective tables be written as ordinary literals instead of carrying a
// decomposed twin for every umlaut.
//
// Browser-bound (the readiness barrel ships to the SPA): pure string work, no
// `node:crypto`, and no regex lookbehind anywhere below — Safari 14 throws on
// one at parse time, which vitest on Node never sees. A test in this module's
// suite reads the source back and asserts the construct is absent.
// ---------------------------------------------------------------------------

/** The three story positions whose value is spliced into fixed scaffolding. */
export type NarrativeClause = "role" | "means" | "ends";

/**
 * What the caller must do with one clause once this seam has looked at it.
 *
 * The two fields are independent on purpose. `text` fixes the value END of the
 * join (a stray full stop, a lead the template needs mid-sentence); `connective`
 * fixes the TEMPLATE end. A caller that wires only `text` still loses the
 * doubled connectives that were actually observed live — every one of those is
 * an `equivalent` connective, which is stripped from `text` — and keeps the
 * status quo for the `alternative` case.
 */
export interface ClauseSplice {
  /** The clause to splice, connective-, infinitive- and
   *  seam-punctuation-corrected. */
  readonly text: string;
  /**
   * Whether the template should still emit its own connective ahead of `text`.
   * False when the value opens with a connective of a DIFFERENT class, which
   * the template's own cannot legally precede ("so that to reduce load", "damit
   * um … zu …"): there the value's connective is the one that survives.
   */
  readonly connective: boolean;
}

/**
 * Connectives a clause value may already open with, per position, in either
 * content locale — the agent is English-tuned and answers arrive in the project
 * language, so BOTH show up in either template (which is exactly what the DE
 * template's `strip*` regexes already assume, and what the EN ones do not).
 *
 * `equivalent` says the same thing the template's own connective says, so it is
 * dropped and the template's canonical one stands. `alternative` says something
 * the template's cannot be stacked in front of, so the template stays silent and
 * the user's connective is what the reader sees.
 */
interface ClauseConnectives {
  readonly equivalent: readonly string[];
  readonly alternative: readonly string[];
}

const CLAUSE_CONNECTIVES: Readonly<Record<NarrativeClause, ClauseConnectives>> = {
  // Role keeps the article: "As a user" → "a user", which is what the English
  // template re-composes into "As a user, …". Stripping the article here would
  // hand the template a bare noun it would then have to re-article, and the
  // article is exactly the thing that gets it wrong on a German plural.
  role: { equivalent: ["as", "als"], alternative: [] },
  // The infinitive "to" is NOT part of the connective: "I want to view a list"
  // keeps its "to", because the template's phrase is "I want {means}", not "I
  // want to {means}".
  //
  // Every German entry carries its PRONOUN. A bare verb-first form cannot be
  // told from a capitalized noun of the same spelling, and German has several:
  // "Wünsche der Kundschaft werden erfasst" ("[the] wishes of the customers are
  // recorded") would lose its subject to a "wünsche" entry, and the result is
  // not a clumsy join but a DELETED WORD out of a value the grounding gate
  // greened byte-exact. A verb-first clause that keeps its own connective just
  // reads a little redundantly, which is the cheaper of the two failures.
  means: {
    equivalent: [
      "i would like",
      "i'd like",
      "i want",
      "i need",
      "i wish",
      "ich möchte",
      "möchte ich",
      "ich will",
      "ich brauche",
      "brauche ich",
      "ich benötige",
      "benötige ich",
      "ich wünsche",
      "wünsche ich",
    ],
    alternative: [],
  },
  ends: {
    equivalent: ["so that", "so dass", "sodass", "damit", "so"],
    alternative: ["in order to", "in order that", "to", "um"],
  },
};

/** Longest phrase first, so "so that" is tested before the bare "so" and
 *  "i would like to" before "i would like". */
const orderedPhrases = (c: ClauseConnectives): readonly (readonly [
  string,
  "equivalent" | "alternative",
])[] =>
  [
    ...c.equivalent.map((p) => [p, "equivalent"] as const),
    ...c.alternative.map((p) => [p, "alternative"] as const),
  ].sort((a, b) => b[0].length - a[0].length);

const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");

/** A phrase matcher anchored at the clause start: whitespace between the words
 *  is free-form, the apostrophe accepts every glyph a keyboard emits, and the
 *  lookahead keeps "sodass" from matching inside "sodassen". Lookahead only —
 *  a lookbehind would throw on Safari 14 and vitest would never see it. */
const phraseMatcher = (phrase: string): RegExp =>
  new RegExp(
    `^${phrase
      .split(/\s+/u)
      .map((w) => escapeRe(w).replace(/'/gu, "['’´`]"))
      .join("\\s+")}(?=[\\s,]|$)`,
    "iu",
  );

const CLAUSE_MATCHERS: Readonly<
  Record<
    NarrativeClause,
    readonly { readonly re: RegExp; readonly kind: "equivalent" | "alternative" }[]
  >
> = {
  role: orderedPhrases(CLAUSE_CONNECTIVES.role).map(([p, kind]) => ({
    re: phraseMatcher(p),
    kind,
  })),
  means: orderedPhrases(CLAUSE_CONNECTIVES.means).map(([p, kind]) => ({
    re: phraseMatcher(p),
    kind,
  })),
  ends: orderedPhrases(CLAUSE_CONNECTIVES.ends).map(([p, kind]) => ({
    re: phraseMatcher(p),
    kind,
  })),
};

// After a bare leading "so", these mark it as the degree ADVERB (= as / such /
// very), not the conjunction: "so much time is saved" reads perfectly well after
// the template's "so that", and stripping the "so" would change what the user
// said. Mirrors the same guard on the i18n side.
const SO_IS_ADVERB =
  /^(?:much|many|far|few|little|long|fast|soon|well|very|that\b|as\b|schnell|viele?|weit|wenig|lange?|gut|bald|sehr)/iu;

// A leading "to" is the purpose connective only in front of a verb. In front of
// a determiner or pronoun it is the preposition ("to the customer"), and the
// template's "so that" belongs in front of it after all.
const TO_IS_PREPOSITION =
  /^(?:the|a|an|this|that|these|those|our|your|their|its|his|her|my|me|us|them|him|it|you|everyone|anyone|all|both|each|any)\b/iu;

/**
 * Does the value open with a connective, and of which class? `null` for a clause
 * that carries none. The `rest` is the clause with that connective removed —
 * only meaningful for the `equivalent` class, which is the one that gets
 * stripped.
 */
const openingConnective = (
  clause: NarrativeClause,
  s: string,
): { readonly kind: "equivalent" | "alternative"; readonly rest: string } | null => {
  for (const { re, kind } of CLAUSE_MATCHERS[clause]) {
    const m = re.exec(s);
    if (!m) continue;
    const head = m[0]!;
    const rest = s.slice(head.length).replace(/^[\s,]+/u, "");
    // A clause that is NOTHING but its connective has no content to carry, so
    // there is nothing here to fix.
    if (rest.length === 0) return null;
    const word = head.trim().toLowerCase();
    if (word === "so" && SO_IS_ADVERB.test(rest)) continue;
    if (word === "to" && TO_IS_PREPOSITION.test(rest)) continue;
    // German "um … zu …" is the purpose connective; a bare "um" is "at"/"around"
    // and carries no clause. The "zu" is what tells them apart.
    if (word === "um" && !/\bzu\b/iu.test(rest)) continue;
    return { kind, rest };
  }
  return null;
};

// `looksNonEnglish` guards the two ENGLISH-shaped orthography rules — the
// lead-lowercase below, and the indefinite-article reconciliation on the i18n
// side. It LIVES in i18n.ts because that is where the second caller is and the
// dependency runs one way (`readiness` → `i18n`, never back); it is re-exported
// here so the splice seam reads as one unit.
export { looksNonEnglish };

// A full stop the value carries, sitting exactly where the template puts its own
// punctuation. Closing quotes/brackets ride along so `he said "it works."` closes
// cleanly rather than losing its quote.
const SEAM_FULL_STOP = /\.(["'’”»)\]]*)$/u;

/**
 * Drop a trailing full stop that would collide with the template's own
 * punctuation ("… verloren geht., so that …" / a doubled "..").
 *
 * ONLY a full stop, and only a sentence-ending one. `!` and `?` carry tone the
 * user chose and stay; an ellipsis stays; and a final token with an internal dot
 * ("z.B.", "e.g.") is an abbreviation, not a sentence end, so it stays too. This
 * removes punctuation the template is about to supply — it never removes a word.
 */
export const trimSeamFullStop = (value: string): string => {
  const s = value.trimEnd();
  const m = SEAM_FULL_STOP.exec(s);
  if (!m) return s;
  const head = s.slice(0, s.length - m[0]!.length);
  if (head.length === 0) return s;
  if (/[.…]$/u.test(head)) return s;
  const lastToken = /\S+$/u.exec(head)?.[0] ?? "";
  if (lastToken.includes(".")) return s;
  return head + m[1]!;
};

/**
 * Lowercase a sentence-case lead so the clause reads as a continuation ("I want
 * View a list" → "I want view a list").
 *
 * Left ALONE for: an initialism ("API"), the bare pronoun "I", an
 * internal-capital token ("GitLab"), and any clause that does not look English
 * (`looksNonEnglish`). Residual worth naming rather than discovering: a clause
 * opening on an ordinary English proper noun ("Microsoft Entra ID stays …")
 * still gets lowered — nothing in a single leading token tells it apart from
 * "Microsoft" the sentence start, and inventing a brand list would be a list
 * that is wrong the week after it ships.
 */
const lowercaseSpliceLead = (s: string): string => {
  const firstWord = /^\S+/u.exec(s)?.[0] ?? "";
  if (!/^\p{Lu}\p{Ll}/u.test(firstWord)) return s;
  if (/\p{Lu}/u.test(firstWord.slice(1))) return s;
  if (looksNonEnglish(s)) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
};

/**
 * Base-form verbs a capability or outcome clause opens on when it is written as
 * a COMMAND rather than as a complement.
 *
 * This is the register Loom's own drafts are written in — "Add an audit trail
 * for admin area changes …", "Trace who changed what …" — and the user adopts
 * them with one click, so it is the DEFAULT output, not an edge case. The frame
 * takes a complement ("I want …", "so that …"), so a command spliced raw reads
 * "I want add an audit trail …, so that trace who changed what …", which is
 * exactly what live #1081 says.
 *
 * CLOSED, ENGLISH-ONLY, AND BIASED TOWARD MISSING ONE. THE MEMBERSHIP RULE:
 * the word must have no NOUN or ADJECTIVE reading that could plausibly open a
 * PRODUCT clause without a determiner. Two halves, and both are load-bearing:
 *
 *  • "no noun or adjective reading" is why `access`, `support`, `export`,
 *    `import`, `use`, `review` and `change` are absent however verb-like they
 *    look — "access to the archive is logged" opens on a mass noun, and a
 *    marker in front of it would break a clause that was fine. It is also why
 *    `archive`, `block`, `capture`, `edit`, `handle`, `restore`, `schedule`,
 *    `select`, `show`, `store`, `sync`, `track`, `update` and `upload` were
 *    removed: "Update notifications are sent", "Track changes stay visible",
 *    "Block storage is provisioned per project", "Select customers get early
 *    access" are all ordinary sentences this list would have broken.
 *  • "plausibly … a PRODUCT clause" is the half that keeps the rule operable.
 *    Almost every English verb can be pressed into a compound ("send button",
 *    "delete key"), and almost every one has SOME noun sense in SOME register
 *    ("trace amounts", the castle "keep", the episcopal "see", a tennis "let").
 *    Disqualifying on those would empty the list and give back the live #1081
 *    prose. The test is whether a `means` / `ends` value in this product could
 *    open on the noun reading — "Track changes stay visible" could; "Trace
 *    amounts of caffeine remain" could not.
 *
 * A SECOND, non-lexical gate stands behind this one: `spliceNarrativeClause`
 * marks nothing that `carriesOwnSubject` reads as a sentence of its own, so a
 * noun reading carrying a finite verb ("Archive access IS logged") is refused
 * even for a word the list still contains. The list is the first line, not the
 * only one.
 *
 * A verb this list misses reads exactly as it does today; a word it wrongly
 * contains reads worse. So the list stays short.
 */
const BARE_VERB_OPENERS: ReadonlySet<string> = new Set([
  "add", "allow", "avoid", "cancel", "collect", "compare", "configure",
  "confirm", "connect", "create", "decide", "define", "delete", "deliver",
  "describe", "detect", "disable", "discover", "enable", "ensure", "evaluate",
  "expose", "generate", "identify", "implement", "improve", "include",
  "inform", "introduce", "investigate", "keep", "know", "let", "maintain",
  "migrate", "notify", "prevent", "provide", "publish", "receive", "reduce",
  "remember", "remove", "rename", "replace", "restrict", "retrieve", "see",
  "send", "submit", "tell", "trace", "understand", "validate", "verify",
  "warn",
]);

/** The clause's first word, letters only and lowercased. */
const leadWord = (s: string): string =>
  (/^\S+/u.exec(s)?.[0] ?? "").replace(/[^\p{L}']/gu, "").toLowerCase();

/**
 * Does this clause open on a bare verb — a command where the frame needs a
 * complement? Requires something AFTER the verb: a one-word clause is a noun as
 * often as a verb, and there is nothing to be gained by marking it.
 *
 * LEXICAL ONLY, on purpose: it answers "is this word in the list", nothing
 * else. `spliceNarrativeClause` below is its one production caller and adds the
 * shape gate. Exported for its own unit test, which is where each word the list
 * keeps and each word it drops is checked against a clause using that word's
 * noun reading — the membership rule is the thing under test, and the splice
 * cannot show it (the shape gate, the connective strip and the clause-role
 * exemption all sit in between).
 */
export const opensWithBareVerb = (value: string | undefined): boolean => {
  const s = (value ?? "").trim();
  if (!/\s/u.test(s)) return false;
  return BARE_VERB_OPENERS.has(leadWord(s));
};

/**
 * Prepare one story clause for the splice: how it should read, and whether the
 * template still owes it a connective.
 *
 * Pure, and a no-op on an already-clean clause ("a user", "list issues"), so the
 * output for a well-formed value is unchanged byte-for-byte.
 *
 * `lowercaseLead` is the TEMPLATE's knob (English on, German off) and is only
 * ever a request — `looksNonEnglish` can still refuse it, which is what keeps an
 * English template from de-capitalizing German content.
 *
 * `infinitiveMarker` is the same kind of knob for the same kind of repair, one
 * layer up: it is the function word the TEMPLATE supplies ("to") when the value
 * is a command and the frame wants a complement. It NEVER touches a word of the
 * value — it prepends one of the template's own, exactly as `compose` prepends
 * "I want". On `ends` it also silences the template's connective, because "to …"
 * is the `alternative` class the frame cannot stand in front of: "so that to
 * trace …" would be the same defect one word later.
 *
 * TWO GATES, and the second one is why the marker is safe on `ends` at all. A
 * command has no subject; a clause that HAS one is not a command however its
 * first word reads, so `carriesOwnSubject` vetoes the marker. Without it the
 * lexical list alone decided, and on `ends` a wrong yes costs two things at
 * once: the marker goes in AND the template's "so that" comes out ("Archive
 * access is logged" → ", to archive access is logged"). It is also what makes
 * the marker and the composer's colon frame mutually exclusive: the frame is
 * chosen on exactly this predicate, so at most one of them can fire.
 */
export const spliceNarrativeClause = (
  clause: NarrativeClause,
  value: string | undefined,
  opts: {
    readonly lowercaseLead?: boolean;
    readonly infinitiveMarker?: string;
  } = {},
): ClauseSplice => {
  const s = (value ?? "").normalize("NFC").trim();
  if (s.length === 0) return { text: "", connective: true };
  const opening = openingConnective(clause, s);
  // `equivalent` → the template's canonical connective replaces the value's, so
  // the sentence is in one voice. `alternative` → the value's connective is the
  // only one that can stand there, so the template must stay silent.
  const stripped = opening?.kind === "equivalent" ? opening.rest : s;
  let connective = opening?.kind !== "alternative";
  let text = trimSeamFullStop(stripped);
  if (opts.lowercaseLead) text = lowercaseSpliceLead(text);
  // Never on an `alternative` opening: there the value already owns the marker
  // ("to reduce the support load", "um … zu …") and the template is already
  // silent. An `equivalent` opening has been STRIPPED by this point, so what is
  // left can perfectly well be the command that needs one ("I want add an export
  // button" → "I want to add an export button").
  if (
    opts.infinitiveMarker !== undefined &&
    clause !== "role" &&
    opening?.kind !== "alternative" &&
    opensWithBareVerb(text) &&
    !carriesOwnSubject(text)
  ) {
    text = `${opts.infinitiveMarker} ${text}`;
    if (clause === "ends") connective = false;
  }
  return { text, connective };
};

// ---------------------------------------------------------------------------
// The `means` SHAPE bound — the other half of the same join.
//
// The frame is "I want {means}", so `means` has to be a COMPLEMENT. Two shapes
// cannot be one, and they fail differently:
//
//   • a COMMAND ("Add an audit trail …") — repaired above, by the template
//     supplying its own "to". No refusal: the value is fine, the join was not.
//   • a full CLAUSE carrying its own subject and finite verb ("Die Kundschaft
//     findet ihre Lieferadresse … und kann sie überschreiben") — live #1078,
//     written as "I want Die Kundschaft findet …". No function word repairs
//     that; the sentence has two subjects and the second one is not going away.
//     This is what `isMeansShaped` refuses.
//
// `ends` gets NO such bound, deliberately: "so that {ends}" takes a clause by
// construction ("so that the customer can find their address"), so the same rule
// there would refuse the CORRECT shape.
// ---------------------------------------------------------------------------

/**
 * Finite verb forms — the tell that a clause has a subject of its own.
 *
 * Closed, and closed on a rule: only forms that CANNOT appear in an infinitive
 * phrase. The German modal infinitives (`können`, `müssen`, `sollen`, `werden`,
 * `haben`) are absent because "die Adresse überschreiben können" is exactly the
 * fragment shape the frame wants; their finite twins (`kann`, `muss`, `soll`,
 * `wird`) are the ones that mark a clause.
 */
const FINITE_VERBS: ReadonlySet<string> = new Set([
  "is", "are", "was", "were", "has", "have", "had",
  "will", "shall", "would", "should", "can", "could", "may", "might", "must",
  "do", "does", "did",
  "ist", "sind", "war", "waren", "hat", "hatte", "hatten",
  "wird", "kann", "muss", "soll", "darf", "gibt",
  "findet", "sieht", "bleibt", "geht", "macht", "zeigt", "braucht",
]);

/**
 * Words that introduce a SUBORDINATE clause: a finite verb after one of these
 * belongs to an embedded clause, and the value as a whole is still a fragment
 * ("the address that is stored at checkout").
 *
 * The German RELATIVE pronouns (`die`, `der`, `das`, …) are deliberately NOT
 * here — they are also the definite articles, and "Die Kundschaft findet …" is
 * the exact sentence this bound exists to refuse. German writes a comma in front
 * of every relative clause, so the comma rule already covers them.
 */
const SUBORDINATORS: ReadonlySet<string> = new Set([
  "that", "which", "who", "whom", "whose", "where", "when", "while", "if",
  "whether", "because", "so", "after", "before", "until", "unless",
  "dass", "wo", "wenn", "ob", "weil", "welche", "welcher", "welches", "damit",
]);

/** Determiners. A SECOND one before the finite verb means the verb's subject is
 *  a noun phrase of its own, i.e. an embedded clause with the relative pronoun
 *  dropped ("a list the team can filter"). */
const DETERMINERS: ReadonlySet<string> = new Set([
  "the", "a", "an", "this", "that", "these", "those", "every", "each", "all",
  "any", "our", "your", "their", "its", "his", "her", "my", "no", "some",
]);

/** Subject pronouns. One mid-clause is the same signal as a second determiner
 *  ("an export I can download"); one at the START is the clause's own subject. */
const SUBJECT_PRONOUNS: ReadonlySet<string> = new Set([
  "i", "we", "you", "they", "he", "she", "it",
  "ich", "wir", "du", "sie", "er", "es", "man",
]);

/** The infinitive marker: the verb after it is not finite ("the ability to do
 *  everything from one screen"). */
const INFINITIVE_MARKERS: ReadonlySet<string> = new Set(["to", "zu"]);

const wordOnly = (t: string): string =>
  t.replace(/[^\p{L}']/gu, "").toLowerCase();

/**
 * Is this clause a SENTENCE OF ITS OWN — a subject plus its finite verb — as
 * opposed to a fragment the frame can splice after a connective?
 *
 * SHAPE ONLY, and precision-biased the same way `isRoleShaped` is: it says yes
 * ONLY when the FIRST finite verb in the clause has an OPENING NOUN PHRASE in
 * front of it to be the subject of. Anything that suggests the verb belongs to
 * an embedded clause — a comma, a subordinator, a second determiner, a
 * mid-clause subject pronoun, an infinitive marker — reads as a fragment, and so
 * does a VERB-INITIAL clause, which has no subject at all. A false yes moves a
 * well-formed story into an unusual frame and, at the mint bound, puts a
 * question in front of a user who already answered; a false no reads exactly as
 * it does today.
 *
 * Residual worth naming: an English clause whose finite verb is missing from the
 * closed list ("the dashboard updates nightly") reads as a fragment. The list
 * covers the auxiliaries and modals a subject-carrying sentence almost always
 * contains, not English morphology.
 *
 * TWO CALLERS, one before the value is accepted and one after it already has
 * been: the `means` mint bound below refuses this shape, and the STORY COMPOSER
 * (`NarrativeTemplate.composeStory`) picks a frame that can carry it — because
 * the supersede rule lets a user's own later words past the mint bound, and
 * "I want Die Kundschaft findet …" must not be the consequence.
 */
export const carriesOwnSubject = (clause: string): boolean => {
  const words = clause.split(/\s+/u).filter((w) => w.length > 0);
  if (words.length < 4) return false;
  for (let i = 0; i < words.length; i += 1) {
    if (!FINITE_VERBS.has(wordOnly(words[i]!))) continue;
    // A clause that OPENS on the finite verb has no subject in front of it, so
    // by this predicate's own definition it does not carry one — it is verb-
    // initial, which in this slot is the ordinary way to write a capability:
    // "can export their orders to CSV", "have a single dashboard", and in German
    // the standard idiom "kann die Lieferadresse überschreiben". Reading those
    // as sentences refused them at the mint bound — a question put in front of a
    // user for a perfectly good fragment, which is the re-ask class this whole
    // pass removes — and pushed the composer onto the colon frame ("Als
    // Kundschaft: kann die Adresse überschreiben"). False here is the cheap
    // direction: it reads exactly as it does today.
    if (i === 0) return false;
    const before = words.slice(0, i);
    // Everything after a comma is an aside or a subordinate clause.
    if (before.some((t) => t.includes(","))) return false;
    const prev = wordOnly(before[before.length - 1]!);
    if (INFINITIVE_MARKERS.has(prev)) return false;
    if (before.some((t) => SUBORDINATORS.has(wordOnly(t)))) return false;
    // Index > 0 only: a leading pronoun IS the clause's own subject.
    if (before.slice(1).some((t) => SUBJECT_PRONOUNS.has(wordOnly(t)))) return false;
    if (before.slice(1).some((t) => DETERMINERS.has(wordOnly(t)))) return false;
    return true;
  }
  return false;
};

/**
 * Does this value have the SHAPE of a capability — something that completes "I
 * want …" — rather than a sentence of its own? The mint-only bound on `means`.
 *
 * A FULL SENTENCE is the only shape refused. The other way a `means` reads
 * wrong once spliced is a bare command whose verb is outside
 * `BARE_VERB_OPENERS`, so the infinitive marker never lands: live #1085 filed
 * "As Users, I want export mine data". That shape is NOT refused here, and the
 * reason is worth writing down because the obvious fix is a trap.
 *
 * Widening `BARE_VERB_OPENERS` cannot work — round 1 had to REMOVE 14 words
 * from it for their noun readings, so the list can never be completed. The next
 * idea is to refuse a lead word from that same ambiguous set instead of
 * repairing it, on the theory that a refusal only costs the model a retry. It
 * does not survive contact with this product's vocabulary: the ambiguous set is
 * `access, change, edit, export, filter, manage, review, share, show, track,
 * update, …` — precisely how capabilities are phrased here (the project map's
 * own nodes are "Manage mines", "Manage projects", "Manage storage locations").
 * A gate on those refuses `manage storage locations`, `update the mine record`
 * and `access to project files without asking a colleague` — the last of which
 * is already grammatical after "I want". It would fire on most issues, and when
 * the user's own wording offers no groundable alternative the model's only exit
 * is to re-ask a field they already answered, which `isRoleShaped`'s docblock
 * weighs as the worse outcome.
 *
 * So the missing "to" stays, deliberately, until the repair happens where the
 * ambiguity is resolvable: at elicitation, where the model knows which reading
 * it meant. `FIELD_RECONSTRUCTORS.means.reason` already asks for that form, and
 * the quote can usually be widened leftward to include it — the user turn
 * behind #1085 contains "to export mine data" verbatim.
 */
export const isMeansShaped = (v: string | undefined): boolean =>
  // Test the clause the way the template will splice it: the frame's own
  // connective is not the value's subject ("I want the list filtered" must not
  // be read as a clause because it contains "want").
  !carriesOwnSubject(spliceNarrativeClause("means", v).text);
